import i18n from '@/utils/i18n';
import { useCallback } from 'react';
import { useToast } from '@usepepper/sprout-kit-ui';

type ToastApi = ReturnType<typeof useToast>;

const NOOP_TOAST_API: ToastApi = {
  clearToasts: () => undefined,
  dismissToast: () => undefined,
  showToast: () => -1,
};

/**
 * SKUI's useToast throws outside a ToastProvider. On native, portaled surfaces
 * (ModalSheet, ActionSheet, Dialog) render outside the experience tree, so
 * fall back to a no-op there instead of crashing the screen.
 */
const useToastOrNoop = (): ToastApi => {
  try {
    return useToast();
  } catch {
    if (__DEV__) {
      console.warn(
        'useExperienceToasts: no ToastProvider in scope, toast dropped. Lift the toast call into the container that renders the sheet or dialog.',
      );
    }
    return NOOP_TOAST_API;
  }
};

export const useExperienceToasts = () => {
  const { clearToasts, dismissToast, showToast } = useToastOrNoop();

  const showErrorToast = useCallback(
    (message?: string) =>
      showToast({
        title: message || i18n.t('toast.standard_error'),
        variant: 'error',
      }),
    [showToast],
  );

  return { clearToasts, dismissToast, showErrorToast, showToast };
};
